/**
 * The writer's input (decision 66 bullets 1–3, decision 44; tables A, N, P, R, S and X in the part 10 log). Every edit
 * the writer makes, however it arrives (table A), goes through `rewrite`, so the chapter stays canonical and text stays
 * text; typed characters that complete a shortcut convert in a transaction of their own (table S); Enter, Shift+Enter,
 * Backspace and Delete follow tables N and R; HTML pasted or dropped from elsewhere goes through the allowlist (table H).
 *
 * Part 09's table I lives on as P6: the writer's text put in between a backslash break's `\` (hidden while the cursor
 * is off its line) and its line end goes before the `\`. An input method's composition passes as it is (I11) and is
 * rewritten once it ends (A3).
 */
import type { ParsedChapter } from '@cockatrice/core';
import { history, historyKeymap, invertedEffects, isolateHistory, redo, standardKeymap, undo } from '@codemirror/commands';
import { Annotation, ChangeSet, EditorSelection, EditorState, Facet, Prec, StateEffect, StateField, Transaction } from '@codemirror/state';
import type { ChangeSpec, Extension, RangeSet, RangeValue, Text, TransactionSpec } from '@codemirror/state';
import { EditorView, keymap, ViewPlugin } from '@codemirror/view';
import type { Command, PluginValue, ViewUpdate } from '@codemirror/view';
import type { Tree } from '@lezer/common';
import { chapterField } from './chapter-state.ts';
import { conversion } from './convert.ts';
import { pageClipboard } from './copy.ts';
import type { ClipboardMemory } from './copy.ts';
import { isSeparator, paragraphLook } from './look.ts';
import { caretLineDecorations, heldField, pendingDecorations, pointerHold, revivePending } from './pending.ts';
import { plainPieces, sourcePieces } from './paste.ts';
import { fastWrite } from './fast.ts';
import { continuation, convert, emptyParagraphAt, prefixLines, rewrite } from './rewrite.ts';
import type { Edit, Piece, Rewritten } from './rewrite.ts';
import { richSource } from './rich-paste.ts';
import { backspace, breakBefore, breakPieces, deleteForward, emptiedPending, enter, keyDeletion, leafAt, pendingEdit, shiftEnter } from './structure.ts';
import type { Edits, LineResult, Pending } from './structure.ts';

/** `pos`, or the start of the backslash hard break it is strictly inside (P6, part 09 I3). Pure. */
export function inputPoint(tree: Tree, pos: number): number {
  let point = pos;
  tree.iterate({
    from: pos,
    to: pos,
    enter: (node) => {
      // The walk enters only nodes that start at or before `pos`; one that starts at it gives `pos` back either way. A
      // break whose first character isn't a backslash is two or more spaces, which show as written (W4).
      if (node.name === 'HardBreak' && node.to > pos && node.to - node.from === 2) point = node.from;
    },
  });
  return point;
}

/** S7: whether typed shortcuts convert. On unless an extension says otherwise; the last one given wins. */
export const autoConvert: Facet<boolean, boolean> = Facet.define<boolean, boolean>({ combine: (values) => values.at(-1) ?? true });

/**
 * This module's own transactions: `rewritten` ones are in canonical form already and pass the filter (A10); a
 * `source` one inserts a source to read with its markup (A6, rich paste).
 */
export const writerInput = Annotation.define<'rewritten' | 'source'>();

/** Sets or clears the pending block or break (N1, N6). */
// Dispatched alone, or with changes it is already placed after (B2), so it is never mapped.
const setPending = StateEffect.define<Pending | null>();

/** N1, N6: what Enter or Shift+Enter left pending; any other change or a selection move drops it. */
export const pendingField: StateField<Pending | null> = StateField.define<Pending | null>({
  create: () => null,
  update: (value, transaction) => {
    for (const effect of transaction.effects) if (effect.is(setPending) || effect.is(revivePending)) return effect.value;
    if (transaction.docChanged) return null;
    // A selection put back where the line is pending (a click on it, focused or not) keeps it (*deep-fix r23*).
    const { selection } = transaction;
    if (selection === undefined || value === null) return value;
    return selection.ranges.length === 1 && selection.main.empty && selection.main.head === value.at ? value : null;
  },
  provide: (field) => [
    // Drawn again when the look changes (part 10a, W11).
    // A line pending when a press began stays until the button is released (part 10a, C9).
    EditorView.decorations.compute([field, heldField, paragraphLook], (state) => {
      const pending = state.field(field);
      return pending === null ? pendingDecorations(state, state.field(heldField)?.pending ?? null, false) : pendingDecorations(state, pending);
    }),
    EditorView.editorAttributes.from(field, (pending) => (pending === null ? {} : { class: 'ct-pending' })),
  ],
});

/** S6: a block conversion just made, so Backspace right after it turns it back; nothing since may have happened. */
interface Converted {
  readonly doc: Text;
  readonly head: number;
}

/** Marks a transaction as a block conversion (S6). */
const markConverted = StateEffect.define();

/** S6: the block conversion just made, if nothing has happened since. */
export const convertedField: StateField<Converted | null> = StateField.define<Converted | null>({
  create: () => null,
  update: (value, transaction) => {
    if (transaction.effects.some((effect) => effect.is(markConverted))) return { doc: transaction.newDoc, head: transaction.newSelection.main.head };
    // Any change or selection after it, even one that comes back, ends "right after".
    return transaction.docChanged || transaction.selection !== undefined ? null : value;
  },
});

/** A3: an input method's composition in progress: the parse before it and every change it has made since. */
interface Composing {
  readonly before: ParsedChapter;
  /** The cursor before it. */
  readonly head: number;
  /** A block or break pending where it started (N1, N6): its text goes in with it. */
  readonly pending: Pending | null;
  readonly changes: ChangeSet;
  /** The selection its last change left: when the writer has moved it since, settling keeps theirs (*deep-fix r3*). */
  readonly selection: EditorSelection;
}

/** Marks a composition settled with nothing to rewrite: it ends there (A3; *deep-fix r2*). */
const compositionSettled = StateEffect.define();

/**
 * A3: the composition in progress, or one that ended and hasn't been settled; from its first change until it is
 * settled, whether or not settling changes the text (the composition lifecycle table, deep-fix r2).
 */
export const compositionField: StateField<Composing | null> = StateField.define<Composing | null>({
  create: () => null,
  update: (value, transaction) => {
    if (transaction.effects.some((effect) => effect.is(compositionSettled))) return null;
    if (!transaction.docChanged) return value;
    if (transaction.isUserEvent('input.type.compose') && transaction.annotation(writerInput) === undefined) {
      return value === null
        ? {
            before: transaction.startState.field(chapterField),
            head: transaction.startState.selection.main.head,
            pending: transaction.startState.field(pendingField),
            changes: transaction.changes,
            selection: transaction.newSelection,
          }
        : { ...value, changes: value.changes.compose(transaction.changes), selection: transaction.newSelection };
    }
    return null;
  },
});

/**
 * A writer's edit as table A sees it (X1, A12): any user event that changes the text but undo and redo (*deep-fix r2*:
 * Ctrl+T's `move.character` was missed when the events were listed).
 */
function isWriterEvent(transaction: Transaction): boolean {
  return transaction.annotation(Transaction.userEvent) !== undefined && !transaction.isUserEvent('undo') && !transaction.isUserEvent('redo');
}

/** The pieces one change of a writer's transaction puts in (table A). */
function piecesOf(transaction: Transaction, inserted: string, memory: ClipboardMemory): Piece[] {
  if (inserted === '') return [];
  const source = transaction.annotation(writerInput) === 'source';
  const arrived = transaction.isUserEvent('input.paste') || transaction.isUserEvent('input.drop') || transaction.isUserEvent('move.drop');
  if (source) return sourcePieces(inserted);
  // A5: this page's own copy, as its own parse reads it where that shows what the copy showed (*deep-fix r9*).
  const copy = arrived ? memory.recalled : null;
  if (copy?.source === inserted) return sourcePieces(inserted, copy);
  // A line copied with nothing selected comes back as a line: CodeMirror pastes it, with a line end, at a line's start
  // (part 09, C15; *deep-fix r1*).
  if (copy !== null && inserted === `${copy.source}\n`) return [...sourcePieces(copy.source, copy), { kind: 'block', text: '\n\n' }];
  return plainPieces(inserted);
}

/** One change of a transaction: in its start document, and where its text lies in the new one. */
interface Change {
  readonly fromA: number;
  readonly toA: number;
  readonly fromB: number;
  readonly toB: number;
  readonly inserted: string;
}

function changesOf(changes: ChangeSet): Change[] {
  const list: Change[] = [];
  changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
    list.push({ fromA, toA, fromB, toB, inserted: inserted.toString() });
  });
  return list;
}

/** A writer's edit, with the change of the transaction it came from. */
type ChangeEdit = Edit & { readonly change: Change };

/** The new selection after `rewritten`: ends at a change's ends follow the edit; others map through. */
function selectionAfter(transaction: Transaction, rewritten: Rewritten<ChangeEdit>): EditorSelection {
  const redo = ChangeSet.of(rewritten.changes, transaction.startState.doc.length);
  const back = transaction.changes.invertedDesc;
  const end = (pos: number): number => {
    const ending = rewritten.points.find((p) => p.edit.change.toB === pos);
    if (ending !== undefined) return ending.end;
    const starting = rewritten.points.find((p) => p.edit.change.fromB === pos);
    if (starting !== undefined) return starting.start;
    return redo.mapPos(back.mapPos(pos));
  };
  const selection = transaction.newSelection;
  return EditorSelection.create(
    selection.ranges.map((range) => EditorSelection.range(end(range.anchor), end(range.head))),
    selection.mainIndex,
  );
}

/** The rewritten form of a writer's transaction (tables A, P, R): the same event, canonical changes. */
function rewriteTransaction(transaction: Transaction, memory: ClipboardMemory): Transaction | TransactionSpec | readonly TransactionSpec[] {
  const state = transaction.startState;
  const parsed = state.field(chapterField);
  const pending = state.field(pendingField);
  const moves = transaction.isUserEvent('input') || transaction.isUserEvent('move');
  const list = changesOf(transaction.changes);
  const event = transaction.annotation(Transaction.userEvent);
  const annotations = [writerInput.of('rewritten'), ...(event === undefined ? [] : [Transaction.userEvent.of(event)])];
  // N8 (*deep-fix r3*): with a line pending, a deletion or a move of characters acts on that empty line the writer sees,
  // not on the text before it: the line goes, nothing else.
  if (pending !== null && (transaction.isUserEvent('delete') || transaction.isUserEvent('move.character'))) return { effects: setPending.of(null) };
  // Part 10a, C8 (*deep-fix r11*): every other deletion on a blank line (A8: a cut of the line with nothing selected, a
  // line deletion) takes the shown line away and nothing else, as the keys do.
  const exit = transaction.isUserEvent('delete') ? blankExit(state, transaction.isUserEvent('delete.forward')) : null;
  const blank = state.doc.lineAt(state.selection.main.head);
  if (exit !== null && list.every((change) => change.fromA <= blank.to && change.toA >= blank.from)) {
    return { selection: EditorSelection.cursor(exit), annotations, scrollIntoView: true };
  }
  // Table F: one character typed or deleted between text needs no rewrite (principle 4), at most an escape.
  const [only, ...more] = list;
  const plain = transaction.annotation(writerInput) === undefined && memory.recalled?.source !== only?.inserted;
  const fast = only !== undefined && more.length === 0 && pending === null && plain ? fastWrite(parsed, only.fromA, only.toA, only.inserted) : null;
  if (fast === only?.inserted) return transaction;
  if (only !== undefined && fast !== null) {
    return {
      changes: { from: only.fromA, to: only.toA, insert: fast },
      selection: EditorSelection.cursor(only.fromA + fast.length),
      effects: transaction.effects,
      scrollIntoView: transaction.scrollIntoView,
      annotations,
    };
  }
  // P2, P3: only typing edits a value or a mark as markup; a paste, a drop or a source never does.
  const typed = !(transaction.isUserEvent('input.paste') || transaction.isUserEvent('input.drop') || transaction.isUserEvent('move.drop')) && transaction.annotation(writerInput) === undefined;
  // P12: blocks pasted or dropped go in as Enter's split leaves room for them (*deep-fix r25*, the user's choice).
  // A drag-move is the dragged range's removal and the insert (*deep-fix r26*); a pending break gives way to the blocks.
  const dropped = list.find((c) => c.inserted !== '');
  const moved = list.length === 2 && transaction.isUserEvent('move.drop') ? list.find((c) => c !== dropped) : undefined;
  const single = list.length === 1 || moved !== undefined;
  const blocks = !typed && dropped !== undefined && single && (pending === null || pending.kind === 'break') ? blockPaste(transaction, dropped, moved, piecesOf(transaction, dropped.inserted, memory), annotations) : null;
  if (blocks !== null) return blocks;
  // R9: a key's deletion keeps the marks of the leaf it is in (*deep-fix r13*).
  const byKey = transaction.isUserEvent('delete.backward') || transaction.isUserEvent('delete.forward');
  const edits: ChangeEdit[] = list.map((change) => {
    const kept = byKey && change.inserted === '' ? keyDeletion(parsed, change.fromA, change.toA) : { from: change.fromA, to: change.toA };
    const from = moves ? inputPoint(parsed.tree, kept.from) : kept.from;
    const to = Math.max(from, moves ? inputPoint(parsed.tree, kept.to) : kept.to);
    const insert = piecesOf(transaction, change.inserted, memory);
    // N1, N6: text typed or pasted where a block or break is pending goes in with it.
    if (pending !== null && insert.length > 0 && from === pending.at && to === pending.at) return { ...pendingEdit(pending, insert), change };
    return { from, to, insert, place: true, typed, change };
  });
  const rewritten = rewrite(parsed, edits);
  // R11: a deletion that empties a paragraph after another leaves a block pending where it was (*deep-fix r21*).
  const emptied = transaction.isUserEvent('delete') && only !== undefined && more.length === 0 ? emptiedPending(parsed, only.fromA, only.toA) : null;
  const selection = emptied === null ? selectionAfter(transaction, rewritten) : EditorSelection.single(emptied.at);
  return {
    changes: rewritten.changes,
    selection,
    effects: [...transaction.effects, ...(emptied === null ? breakLeft(transaction, parsed, rewritten, selection) : [setPending.of(emptied)])],
    scrollIntoView: transaction.scrollIntoView,
    annotations,
  };
}

/**
 * Table B, B2 (*deep-fix r7*): a deletion of all the text after a backslash break takes the break with it; the cursor
 * stays where the writer sees it, on that empty line, with the break pending there again (N6), as before that text was
 * typed.
 */
function breakLeft(transaction: Transaction, parsed: ParsedChapter, rewritten: Rewritten<ChangeEdit>, selection: EditorSelection): StateEffect<Pending | null>[] {
  const [only, ...more] = changesOf(transaction.changes);
  if (!transaction.isUserEvent('delete') || only === undefined || more.length > 0 || selection.ranges.length > 1 || !selection.main.empty) return [];
  const at = breakBefore(parsed, only.fromA);
  if (at === null || !rewritten.changes.some((change) => change.from <= at && change.to > at)) return [];
  const cursor = selection.main.head;
  return [setPending.of({ at: cursor, from: cursor, open: breakPieces(parsed, at), kind: 'break', out: 0 })];
}

/**
 * P12 (*deep-fix r25*, the user's choice): pieces that start with a block's marks (a pasted or dropped heading, list,
 * quote or scene break) go in as whole blocks: the selection goes first, then the point is split exactly as Enter splits
 * it (N1: inside a span, a centered block, a heading, a quote or an item; a block pending after a leaf's end or above
 * its start), and the blocks go between the two halves, or after or before the leaf. Where Enter does nothing (a link's
 * value), null: the paste goes in as any other.
 */
function blockPaste(transaction: Transaction, change: Change, moved: Change | undefined, pieces: readonly Piece[], annotations: readonly Annotation<unknown>[]): TransactionSpec[] | null {
  const [head] = pieces;
  if (head?.kind !== 'block' || head.keep === true || !/^[^\n]*\S/u.test(head.text)) return null;
  const start = transaction.startState;
  const parsedBefore = start.field(chapterField);
  // The selection, or the dragged range, goes first, as a deletion of its own.
  const gone = moved ?? (change.toA > change.fromA ? change : null);
  const removed = gone === null ? null : rewrite(parsedBefore, [{ from: gone.fromA, to: gone.toA, insert: [] }]);
  const cleared = removed === null ? start : start.update({ changes: removed.changes, annotations: [...annotations] }).state;
  const point =
    removed === null
      ? change.fromA
      : moved === undefined
        ? removed.points.reduce((_, p) => p.end, change.fromA)
        : ChangeSet.of(removed.changes, start.doc.length).mapPos(change.fromA);
  const parsed = cleared.field(chapterField);
  // Part 10b, T1 (*deep-fix r1*): at an empty paragraph, the blocks take its place.
  const empty = emptyParagraphAt(parsed, point);
  if (empty !== null) {
    return blockSpecs(removed, annotations, rewrite(parsed, [{ from: empty.from, to: empty.to, insert: prefixLines(pieces, continuation(parsed, empty.from)) }]), empty.from);
  }
  // A point before a block's own marks at its line's start (`->`, `- `, `> `, `[^1]:`): the blocks go before that line's
  // blocks, as blocks of their own, and the line follows after a break.
  let at = point;
  for (let k = point, end = parsed.text.indexOf('\n', point); k <= (end < 0 ? parsed.text.length : end); k += 1) {
    if (leafAt(parsed, k)?.contentFrom === k) {
      at = k;
      break;
    }
  }
  if (at > point) {
    const outer = continuation(parsed, point);
    const before = prefixLines([...pieces, { kind: 'block', text: '\n\n' }], outer);
    return blockSpecs(removed, annotations, rewrite(parsed, [{ from: point, to: point, insert: before }]), point);
  }
  const blocks = prefixLines(pieces, continuation(parsed, at));
  // A leaf the selection emptied (a heading's or centered block's text all selected) goes: the blocks take its place.
  const leaf = leafAt(parsed, at);
  if (leaf !== null && leaf.contentFrom === leaf.contentTo && removed !== null) {
    return blockSpecs(removed, annotations, rewrite(parsed, [{ from: leaf.node.from, to: leaf.node.to, insert: blocks }]), at);
  }
  const split = enter(parsed, at, null);
  if (split === null) return null;
  let edit: Edit;
  if ('pending' in split) {
    edit = pendingEdit(split.pending, blocks);
  } else {
    // The split's own markup, between the spans it closes and opens: the break before the second half, and its marks.
    const [first] = split.edits;
    const marks = first.insert.filter((p) => p.kind === 'block');
    const joined = marks.map((p) => p.text).join('');
    const lineEnd = joined.indexOf('\n');
    // An empty item or quote line Enter would take away: the blocks take its place.
    if (lineEnd < 0) {
      const line = prefixLines([{ kind: 'block', text: '\n\n' }, ...pieces], continuation(parsed, first.from));
      return blockSpecs(removed, annotations, rewrite(parsed, [{ from: first.from, to: first.to, insert: line }]), at);
    }
    const close = first.insert.slice(0, first.insert.findIndex((p) => p.kind === 'block'));
    const open = first.insert.slice(first.insert.findLastIndex((p) => p.kind === 'block') + 1);
    const before: Piece = { kind: 'block', text: joined.slice(0, joined.lastIndexOf('\n') + 1) + continuation(parsed, at), prefixed: true, keep: true };
    const after: Piece = { kind: 'block', text: joined.slice(lineEnd), prefixed: true, keep: true };
    edit = { ...first, insert: [...close, before, ...blocks, after, ...open] };
  }
  return blockSpecs(removed, annotations, rewrite(parsed, [edit]), at);
}

/** P12's transactions: the selection's removal, if any, then the blocks, the cursor after them. */
function blockSpecs(removed: Rewritten<Edit> | null, annotations: readonly Annotation<unknown>[], placed: Rewritten<Edit>, at: number): TransactionSpec[] {
  const end = placed.points.reduce((_, p) => p.end, at);
  return [
    ...(removed === null ? [] : [{ changes: removed.changes, annotations: [...annotations] }]),
    { changes: placed.changes, selection: EditorSelection.cursor(end), scrollIntoView: true, annotations: [...annotations], sequential: true },
  ];
}

/** A3: the edits a composition made, as typed text, in the positions of the parse before it. */
function compositionEdits(composing: Composing): Edit[] {
  const { pending } = composing;
  const { tree } = composing.before;
  return changesOf(composing.changes).map((c) => {
    const insert = plainPieces(c.inserted);
    // N1, N6: text composed where a block or break was pending goes in with it, as typed text does.
    if (pending !== null && insert.length > 0 && c.fromA === pending.at && c.toA === pending.at) return pendingEdit(pending, insert);
    // P6: right after a backslash break's `\`, before it, as typed text goes (*deep-fix r9*, the user's choice).
    const from = inputPoint(tree, c.fromA);
    return { from, to: Math.max(from, inputPoint(tree, c.toA)), insert, place: true, typed: true };
  });
}

/** The source and cursor a composition's text settles into (A3), from the parse before it. */
function settle(composing: Composing): { text: string; head: number } {
  const rewritten = rewrite(composing.before, compositionEdits(composing));
  let text = composing.before.text;
  for (const change of [...rewritten.changes].reverse()) text = text.slice(0, change.from) + change.insert + text.slice(change.to);
  // The cursor after the last edit, or where it was when the composition changed nothing in the end.
  return { text, head: rewritten.points.reduce((_, p) => p.end, composing.head) };
}

/** The one change from `doc` to `text`: what differs between their common start and end (G6). */
function diff(doc: string, text: string): ChangeSpec {
  let head = 0;
  while (head < doc.length && head < text.length && doc.charCodeAt(head) === text.charCodeAt(head)) head += 1;
  let tail = 0;
  while (tail < doc.length - head && tail < text.length - head && doc.charCodeAt(doc.length - 1 - tail) === text.charCodeAt(text.length - 1 - tail)) tail += 1;
  return { from: head, to: doc.length - tail, insert: text.slice(head, text.length - tail) };
}

/** The chapter's text as the autosave stores it: a composition in progress settled (A3, principle 1). */
export function settledText(state: EditorState): string {
  const composing = state.field(compositionField, false) ?? null;
  return composing === null ? state.doc.toString() : settle(composing).text;
}

/** Table X, X1: the writer's transactions rewritten; everything else as it is. */
function writerFilter(memory: ClipboardMemory): Extension {
  return EditorState.transactionFilter.of((transaction) => {
    if (!transaction.docChanged || transaction.annotation(writerInput) === 'rewritten') return transaction;
    if (transaction.annotation(writerInput) === undefined && !isWriterEvent(transaction)) return transaction;
    if (transaction.isUserEvent('input.type.compose')) return transaction;
    const composing = transaction.startState.field(compositionField);
    if (composing !== null) return settleFirst(transaction, composing);
    return rewriteTransaction(transaction, memory);
  });
}

/** Whether a command's edits hold any. */
function isEdits(edits: Edits | readonly []): edits is Edits {
  return edits.length > 0;
}

/**
 * A3: another edit before a composition settled: the composition settles first, then the edit is made as itself (its
 * own pieces), in one transaction (*deep-fix r2*).
 */
function settleFirst(transaction: Transaction, composing: Composing): TransactionSpec[] {
  const start = transaction.startState;
  const settled = settle(composing);
  const repair = ChangeSet.of(diff(start.doc.toString(), settled.text), start.doc.length);
  const repaired = start.update({
    changes: repair,
    selection: EditorSelection.cursor(settled.head),
    // It ends here even when settling changes nothing. No line is pending: a compose change drops one, and the line
    // commands, the only ones to leave one, settle first (*deep-fix r5*).
    effects: compositionSettled.of(null),
    annotations: [writerInput.of('rewritten'), Transaction.userEvent.of('input.type.compose')],
  });
  // The edit's changes and selection, carried over the settling. A point at the composition's end goes where its settled
  // text ends (*deep-fix r17*): what settling put in there (an escape's second `\`, the break after a block pending
  // above) is the composition's, and the edit comes after it, as typing after it would.
  const end = composing.selection.main.head;
  const settledAt = (pos: number, assoc: -1 | 1): number => (pos === end ? settled.head : repair.mapPos(pos, assoc));
  const specs: ChangeSpec[] = [];
  transaction.changes.iterChanges((fromA, toA, _fromB, _toB, inserted) => {
    const from = settledAt(fromA, -1);
    specs.push({ from, to: Math.max(from, settledAt(toA, 1)), insert: inserted });
  });
  const changes = ChangeSet.of(specs, repaired.state.doc.length);
  const back = transaction.changes.invertedDesc;
  const carry = (pos: number): number => changes.mapPos(settledAt(back.mapPos(pos, -1), -1), 1);
  const selection = EditorSelection.create(
    transaction.newSelection.ranges.map((range) => EditorSelection.range(carry(range.anchor), carry(range.head))),
    transaction.newSelection.mainIndex,
  );
  const event = transaction.annotation(Transaction.userEvent);
  const source = transaction.annotation(writerInput);
  const next = repaired.state.update({
    changes,
    selection,
    scrollIntoView: transaction.scrollIntoView,
    annotations: [...(event === undefined ? [] : [Transaction.userEvent.of(event)]), ...(source === undefined ? [] : [writerInput.of(source)])],
  });
  return [
    { changes: repair, selection: repaired.selection, effects: repaired.effects, annotations: [writerInput.of('rewritten'), Transaction.userEvent.of('input.type.compose')] },
    { changes: next.changes, selection: next.selection, effects: next.effects, scrollIntoView: next.scrollIntoView, annotations: [writerInput.of('rewritten'), ...(event === undefined ? [] : [Transaction.userEvent.of(event)])], sequential: true },
  ];
}

/** Rewrites edits made by a command and dispatches them; the cursor goes to the first edit's end. */
function dispatchEdits(view: EditorView, edits: Edits, event: string): void {
  const first = edits.find((edit) => 'cursor' in edit) ?? edits[0];
  const rewritten = rewrite(view.state.field(chapterField), edits);
  view.dispatch({
    changes: rewritten.changes,
    selection: EditorSelection.cursor(rewritten.points.reduce((cursor, p) => (p.edit === first ? p.end : cursor), 0)),
    annotations: [writerInput.of('rewritten'), Transaction.userEvent.of(event)],
    scrollIntoView: true,
  });
}

/** S1–S6: the conversion the character just typed at the cursor completes, as a transaction of its own; or null. */
function conversionOf(state: EditorState, char: string): Transaction | null {
  const head = state.selection.main.head;
  if (!state.facet(autoConvert) || !state.selection.main.empty) return null;
  const parsed = state.field(chapterField);
  const found = conversion(parsed, head, char);
  if (found === null) return null;
  const converted = convert(parsed, found.edits, found.options);
  if (converted === null) return null;
  return state.update({
    changes: converted.changes,
    selection: EditorSelection.cursor(converted.points.reduce((_, p) => p.end, head)),
    effects: char === ' ' ? markConverted.of(null) : [],
    annotations: [writerInput.of('rewritten'), Transaction.userEvent.of('input.convert'), isolateHistory.of('full')],
    scrollIntoView: true,
  });
}

/**
 * X2: typed text goes in as the writer's edit (A1), then table S looks at it. A conversion is a transaction of its own
 * (S6) dispatched with the typing, so the screen updates once for the keystroke (decision 14).
 */
const typedInput: Extension = EditorView.inputHandler.of((view, _from, _to, text, insert) => {
  if (view.composing) return false;
  typeText(view, text, insert());
  return true;
});

/** X2: dispatches `typed`, the text typed, with the conversion it completes (S6) in the same update. */
function typeText(view: EditorView, text: string, typed: Transaction): void {
  // Every shortcut's character is one UTF-16 unit.
  const converted = text.length === 1 ? conversionOf(typed.state, text) : null;
  view.dispatch(converted === null ? [typed] : [typed, converted]);
}

/** What {@link typedOver} reads of a `beforeinput` event. */
export interface BeforeInput {
  readonly inputType: string;
  readonly data: string | null;
  readonly isComposing: boolean;
  readonly cancelable: boolean;
  /** Whether the event is still being dispatched, so a `preventDefault` stops the browser's own edit. */
  readonly live: boolean;
}

/**
 * Table X, X7 (part 41a): the text a `beforeinput` types over a selection, for the editor to put in itself; null to leave
 * the event to the browser. Chromium and WebKit type over a selection that spans lines by moving the paragraph after it
 * as HTML written with inline `style` attributes, which the CSP's `style-src` refuses (a violation report, every time).
 * Taken: plain typing (`insertText`) over a non-empty selection, the browser's selection (`shown`, as positions) being
 * the editor's main one, the event cancelable and live. Left to the browser: a composition (I11: the input method owns
 * its text until it ends, `composing` being CodeMirror's word that one has started), Android (its keyboards' typing is
 * CodeMirror's to read from the DOM; `preventDefault` there is not to be relied on), a cursor (nothing replaced) and a
 * selection the editor hasn't read yet (CodeMirror reads the DOM's, as before). An end of the editor's selection strictly
 * inside an atomic range (`atomic`: an escape, an entity, W1, W2) has no place in the DOM, so the browser shows it at
 * that range's start or end; there either counts as the editor's end, as CodeMirror's own reading of an imprecise DOM
 * point gives its state's position back (fix-next 75). Pure; `atomic` is read only for a selection.
 */
export function typedOver(input: BeforeInput, selection: EditorSelection, shown: { readonly anchor: number; readonly head: number } | null, composing: boolean, android: boolean, atomic: () => readonly RangeSet<RangeValue>[] = () => []): string | null {
  if (input.inputType !== 'insertText' || input.data === null || input.data === '') return null;
  if (input.isComposing || composing || android || !input.cancelable || !input.live) return null;
  const { main } = selection;
  if (main.empty || shown === null) return null;
  if (!showsAt(shown.anchor, main.anchor, atomic) || !showsAt(shown.head, main.head, atomic)) return null;
  return input.data;
}

/**
 * X7: whether the browser's `shown` position stands for the editor's `pos`: the same, or, `pos` being strictly inside an
 * atomic range, either of its ends. A lookup at one position in each set, never a scan.
 */
function showsAt(shown: number, pos: number, atomic: () => readonly RangeSet<RangeValue>[]): boolean {
  if (shown === pos) return true;
  const edges: number[] = [];
  for (const set of atomic()) {
    set.between(pos, pos, (from, to) => {
      if (from < pos && pos < to) edges.push(from, to);
    });
  }
  return edges.includes(shown);
}

/** Whether this browser runs on Android (X7 leaves its typing to the DOM). */
const onAndroid = navigator.userAgent.includes('Android');

/** The browser's selection in the editor, as positions; null when it isn't (all) in the content. */
function shownSelection(view: EditorView): { anchor: number; head: number } | null {
  const content = view.contentDOM;
  const dom = content.ownerDocument.getSelection();
  // `contains` is false for null: no selection, or one with no range.
  const inside = (node: Node | null): node is Node => content.contains(node);
  if (dom === null) return null;
  const { anchorNode, focusNode } = dom;
  if (!inside(anchorNode) || !inside(focusNode)) return null;
  return { anchor: view.posAtDOM(anchorNode, dom.anchorOffset), head: view.posAtDOM(focusNode, dom.focusOffset) };
}

/** X7: typing over a selection goes in as the editor's transaction, never the browser's own replace (`typedOver`). */
const typedOverSelection: Extension = EditorView.domEventHandlers({
  beforeinput: (event, view) => {
    const input: BeforeInput = { inputType: event.inputType, data: event.data, isComposing: event.isComposing, cancelable: event.cancelable, live: event.eventPhase !== Event.NONE };
    const atomic = (): readonly RangeSet<RangeValue>[] => view.state.facet(EditorView.atomicRanges).map((ranges) => ranges(view));
    const text = typedOver(input, view.state.selection, shownSelection(view), view.compositionStarted, onAndroid, atomic);
    if (text === null) return false;
    event.preventDefault();
    typeText(view, text, view.state.update(view.state.replaceSelection(text), { userEvent: 'input.type', scrollIntoView: true }));
    return true;
  },
});

/** What an empty paragraph holds (part 10b, S1). */
const NO_BREAK_SPACE = '\u00a0';

/** The cursor, when nothing is selected. */
function cursorOf(state: EditorState): number | null {
  const main = state.selection.main;
  return main.empty ? main.head : null;
}

/** Applies a line command's result: edits, a pending block or break, or nothing (the key is still taken). */
function applyLine(view: EditorView, result: LineResult): boolean {
  if (result === null) return true;
  if ('pending' in result) {
    view.dispatch({ effects: setPending.of(result.pending) });
    return true;
  }
  dispatchEdits(view, result.edits, 'input');
  if (result.then !== undefined) view.dispatch({ effects: setPending.of(result.then) });
  return true;
}

/** Removes the selection as the writer's deletion; true when there was one. */
function removeSelection(view: EditorView): boolean {
  const main = view.state.selection.main;
  if (main.empty) return false;
  dispatchEdits(view, [{ from: main.from, to: main.to, insert: [] }], 'delete.selection');
  return true;
}

/**
 * Part 10a, table C, C8 (*deep-fix r5, r11*): where a deletion made with the cursor alone on a blank line, which the look
 * shows as an empty new paragraph, leaves it: that paragraph goes, as a pending line does (N8), the cursor to the end of
 * the line before (back) or the start of the line after (forward), and nothing else changes. Null for any other cursor.
 */
function blankExit(state: EditorState, forward: boolean): number | null {
  const head = state.selection.ranges.length === 1 ? cursorOf(state) : null;
  if (head === null) return null;
  const line = state.doc.lineAt(head);
  if (!isSeparator(state.field(chapterField).tree, line)) return null;
  return forward ? Math.min(state.doc.length, line.to + 1) : Math.max(0, line.from - 1);
}

/** C8: a deletion key on a blank line takes the line away (`blankExit`). True when it did. */
function leaveBlank(view: EditorView, forward: boolean): boolean {
  const to = blankExit(view.state, forward);
  if (to === null) return false;
  view.dispatch({ selection: EditorSelection.cursor(to), userEvent: 'select', scrollIntoView: true });
  return true;
}

/** Table N, N1–N4, N7: Enter. */
export const enterCommand: Command = (view) => {
  settleComposition(view);
  removeSelection(view);
  // P6: right after a backslash break's `\`, the key acts before it (*deep-fix r9*).
  const head = inputPoint(view.state.field(chapterField).tree, view.state.selection.main.head);
  const result = enter(view.state.field(chapterField), head, view.state.field(pendingField));
  if (result === null && emptyParagraph(view, head)) return true;
  return applyLine(view, result);
};

/**
 * Part 10b, E1, E3, E4: Enter that would change nothing on a line the writer sees as a new paragraph's (a pending block
 * with no container left, a block pending above, the shown blank line) makes it an empty paragraph: a no-break space
 * typed there, which the rewrite puts in as typing would (N1, P4) and stores as `&nbsp;` (S1), then Enter at its end, so
 * a new pending line follows it. True when it did.
 */
function emptyParagraph(view: EditorView, head: number): boolean {
  const { state } = view;
  const pending = state.field(pendingField);
  const { tree } = state.field(chapterField);
  const line = state.doc.lineAt(head);
  // E4, E4b (*deep-fix r2*): the shown blank line, or the empty chapter's one line.
  const blank = pending === null && (isSeparator(tree, line) || (tree.topNode.firstChild === null && line.text.trim() === ''));
  if (!blank && (pending?.at !== head || pending.kind === 'break')) return false;
  // E1b (*deep-fix r2*) is table S's now (fix-next 6): a block is pending only where its text can be written.
  view.dispatch({ changes: { from: head, insert: NO_BREAK_SPACE }, selection: EditorSelection.cursor(head + 1), userEvent: 'input', scrollIntoView: true });
  const end = view.state.selection.main.head;
  applyLine(view, enter(view.state.field(chapterField), end, null));
  return true;
}

/** Table N, N4–N6: Shift+Enter. */
export const shiftEnterCommand: Command = (view) => {
  settleComposition(view);
  removeSelection(view);
  const head = inputPoint(view.state.field(chapterField).tree, view.state.selection.main.head);
  return applyLine(view, shiftEnter(view.state.field(chapterField), head));
};

/** Table R, R4, R7 and S6: Backspace; otherwise CodeMirror's deletion (R6). */
export const backspaceCommand: Command = (view) => {
  settleComposition(view);
  const { state } = view;
  if (state.field(pendingField) !== null) {
    view.dispatch({ effects: setPending.of(null) });
    return true;
  }
  if (leaveBlank(view, false)) return true;
  const head = cursorOf(state);
  if (head === null) return false;
  if (state.field(convertedField) !== null) return undo(view);
  const edits = backspace(state.field(chapterField), head);
  if (edits === null) return false;
  if (isEdits(edits)) dispatchEdits(view, edits, 'delete.backward');
  return true;
};

/** R4, R7 for word and line deletion: at a block's start as Backspace (never undoing a conversion); otherwise CodeMirror's. */
export const edgeBackspace: Command = (view) => {
  settleComposition(view);
  if (view.state.field(pendingField) !== null) {
    view.dispatch({ effects: setPending.of(null) });
    return true;
  }
  if (leaveBlank(view, false)) return true;
  const head = cursorOf(view.state);
  if (head === null) return false;
  const edits = backspace(view.state.field(chapterField), head);
  if (edits === null) return false;
  if (isEdits(edits)) dispatchEdits(view, edits, 'delete.backward');
  return true;
};

/** Table R, R5, R7: Delete; otherwise CodeMirror's deletion (R6). */
export const deleteCommand: Command = (view) => {
  settleComposition(view);
  // N8: Delete on a pending line takes that line away.
  if (view.state.field(pendingField) !== null) {
    view.dispatch({ effects: setPending.of(null) });
    return true;
  }
  if (leaveBlank(view, true)) return true;
  const head = cursorOf(view.state);
  if (head === null) return false;
  const edits = deleteForward(view.state.field(chapterField), head);
  if (edits === null) return false;
  if (isEdits(edits)) dispatchEdits(view, edits, 'delete.forward');
  return true;
};

/**
 * X3, A6: HTML pasted or dropped from elsewhere, through the allowlist (table H). This page's own copy, text with no
 * HTML, or HTML that holds no text take CodeMirror's own path (the clipboard filters, A4, A5).
 */
function richInput(memory: ClipboardMemory): Extension {
  /** Takes the event's HTML in place of `from`–`to` when table H applies; true when it did. */
  const take = (view: EditorView, event: Event, data: DataTransfer | null, from: number, to: number, kind: string): boolean => {
    if (data === null) return false;
    const html = data.getData('text/html');
    if (html === '' || memory.recall(data.getData('text/plain')) !== null) return false;
    const source = richSource(html);
    if (source === null) return false;
    event.preventDefault();
    view.dispatch({
      changes: { from, to, insert: source },
      selection: EditorSelection.cursor(from + source.length),
      annotations: [writerInput.of('source'), Transaction.userEvent.of(kind)],
      scrollIntoView: true,
    });
    return true;
  };
  return EditorView.domEventHandlers({
    paste: (event, view) => take(view, event, event.clipboardData, view.state.selection.main.from, view.state.selection.main.to, 'input.paste'),
    drop: (event, view) => {
      const at = view.posAtCoords({ x: event.clientX, y: event.clientY });
      return at !== null && take(view, event, event.dataTransfer, at, at, 'input.drop');
    },
  });
}

/**
 * How long after `compositionend` the composition is looked at again: past CodeMirror's own settling of the DOM (a
 * microtask, Android's delayed flush, or its 50 ms clean-up), in case no update follows the end.
 */
export const COMPOSITION_SETTLE_MS = 60;

/**
 * X6, A3: once a composition has ended, its text is rewritten as typed text, after the update that saw it end or, when
 * none follows, a moment after `compositionend`. Nothing is dispatched when the text is already as it would settle, so an
 * input method composing word after word (Android) is never disturbed by its own text.
 */
class CompositionWatch implements PluginValue {
  private queued = false;
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private readonly view: EditorView;

  constructor(view: EditorView) {
    this.view = view;
  }

  update(update: ViewUpdate): void {
    if (!update.view.composing && update.state.field(compositionField) !== null) this.queue();
  }

  /** `compositionend`: looks again once CodeMirror has settled the DOM. */
  ended(): void {
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      this.queue();
    }, COMPOSITION_SETTLE_MS);
    this.timers.add(timer);
  }

  destroy(): void {
    for (const timer of this.timers) clearTimeout(timer);
  }

  private queue(): void {
    if (this.queued) return;
    this.queued = true;
    queueMicrotask(() => {
      this.repair();
    });
  }

  private repair(): void {
    this.queued = false;
    if (!this.view.composing) settleComposition(this.view);
  }
}

/**
 * A3: settles an ended composition now, if one is open: its text rewritten as typed text, then S8. Every key and every
 * line command does this first, so nothing acts on unsettled text (*deep-fix r5*: with EditContext, Android's
 * `compositionend` never reaches the content element and Enter came before any update).
 */
function settleComposition(view: EditorView): void {
  const composing = view.state.field(compositionField);
  if (composing === null) return;
  const result = settle(composing);
  const doc = view.state.doc.toString();
  // Settled with nothing to rewrite, it still ends here: no change, so the input method is left alone.
  if (result.text === doc) {
    view.dispatch({ effects: compositionSettled.of(null) });
    return;
  }
  const changes = ChangeSet.of(diff(doc, result.text), doc.length);
  // The cursor after the settled text, unless the writer moved it since (a tap that ended the composition).
  const moved = !view.state.selection.eq(composing.selection);
  view.dispatch({
    changes,
    selection: moved ? view.state.selection.map(changes) : EditorSelection.cursor(result.head),
    // A compose event joins the composition's undo step however late it comes: one Undo takes both back, to the
    // canonical text before it (A9; *deep-fix r3*, the user's choice).
    annotations: [writerInput.of('rewritten'), Transaction.userEvent.of('input.type.compose')],
  });
  // S8: one character an input method committed at the cursor may complete a shortcut.
  const [only, ...more] = changesOf(composing.changes);
  if (only !== undefined && more.length === 0 && only.fromA === only.toA && only.inserted.length === 1) {
    const converted = conversionOf(view.state, only.inserted);
    if (converted !== null) view.dispatch(converted);
  }
}

const compositionWatch: Extension = ViewPlugin.fromClass(CompositionWatch, {
  eventObservers: {
    compositionend() {
      this.ended();
    },
  },
});

/** The keys of table N and R first, then CodeMirror's cursor, selection, deletion and history keys (X5). */
const keys: Extension = [
  Prec.high(
    keymap.of([
      // A3: any other key, once the input method is done, meets settled text (*deep-fix r5*).
      {
        any: (view) => {
          if (!view.composing) settleComposition(view);
          return false;
        },
      },
      { key: 'Enter', run: enterCommand, shift: shiftEnterCommand },
      // Shift+Backspace is Backspace (*deep-fix r13*: CodeMirror's own took it otherwise).
      { key: 'Backspace', run: backspaceCommand, shift: backspaceCommand },
      { key: 'Delete', run: deleteCommand },
      // Word and line deletion at a block's edge join blocks as Backspace and Delete do (*deep-fix r4, r13*).
      { key: 'Mod-Backspace', mac: 'Alt-Backspace', run: edgeBackspace },
      { mac: 'Mod-Backspace', run: edgeBackspace },
      { key: 'Mod-Delete', mac: 'Alt-Delete', run: deleteCommand },
      { mac: 'Mod-Delete', run: deleteCommand },
      // The Mac's emacs keys CodeMirror binds: Ctrl+H, Ctrl+D, Ctrl+K and Ctrl+Alt+H (*deep-fix r13*).
      { mac: 'Ctrl-h', run: backspaceCommand },
      { mac: 'Ctrl-d', run: deleteCommand },
      { mac: 'Ctrl-k', run: deleteCommand },
      { mac: 'Ctrl-Alt-h', run: edgeBackspace },
      // CodeMirror binds Shift+Mod+Z to Redo on macOS only and falls back to Undo elsewhere; it is Redo everywhere.
      { key: 'Mod-Shift-z', run: redo },
    ]),
  ),
  keymap.of([...standardKeymap, ...historyKeymap]),
];

/**
 * Fix-next 5: the selection an edit leaves, kept with its history event. CodeMirror gives a redo the selection before the
 * edit mapped through it, which is right for an insertion at the cursor; the rewrite replaces a window, and the mapping
 * lands at the window's edge (a blank line, the end of the paragraph before), so the next text went elsewhere. Undo
 * passes the value on to its redo, which puts the cursor back where the edit left it. Grouped keystrokes' effects come
 * newest first, so the first is the last keystroke's. Not mapped: a redo meets only the text the edit made (any new
 * edit drops the redo branch), and a value past the text is ignored.
 */
const afterEdit = StateEffect.define<EditorSelection>();

/** The history's record of each edit's selection, and of its undo's (passed on unchanged). */
const keepAfterEdit = invertedEffects.of((transaction) => {
  const carried = transaction.effects.find((effect) => effect.is(afterEdit));
  if (carried !== undefined) return [carried];
  return transaction.docChanged ? [afterEdit.of(transaction.newSelection)] : [];
});

/** Fix-next 5: after a redo, the selection its edit left, once the redo's own update is done. */
const redoSelection = ViewPlugin.define((view) => ({
  update(update: ViewUpdate): void {
    for (const transaction of update.transactions) {
      if (!transaction.isUserEvent('redo')) continue;
      const kept = transaction.effects.find((effect) => effect.is(afterEdit))?.value;
      const { doc } = update.state;
      if (kept === undefined || kept.ranges.some((range) => range.to > doc.length)) continue;
      queueMicrotask(() => {
        if (view.state.doc === doc) view.dispatch({ selection: kept, userEvent: 'select.redo', scrollIntoView: true });
      });
    }
  },
}));

/** The writer's input: tables A, N, P, R, S and H in the editor, with the history; `autoConversion` per S7. */
export function liveInput(options: { readonly autoConversion?: boolean; readonly memory?: ClipboardMemory } = {}): Extension {
  const memory = options.memory ?? pageClipboard;
  return [
    history(),
    keepAfterEdit,
    redoSelection,
    pendingField,
    convertedField,
    compositionField,
    autoConvert.of(options.autoConversion ?? true),
    writerFilter(memory),
    // Part 10a, table C: the blank line the cursor stands on, shown as the line typing there makes.
    EditorView.decorations.compute(['doc', 'selection', paragraphLook, heldField], caretLineDecorations),
    pointerHold((state) => state.field(pendingField)),
    typedInput,
    typedOverSelection,
    richInput(memory),
    compositionWatch,
    keys,
  ];
}
